import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Tabs,
  Tab,
  Container,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  Menu as MenuIcon,
  Restaurant as RestaurantIcon,
  Book as BookIcon,
  Logout as LogoutIcon,
  Assignment as AssignmentIcon,
  LocalOffer as TagIcon,
  Psychology as AIIcon,
  ExpandMore as ExpandMoreIcon,
  Campaign as SloganIcon,
  People as PeopleIcon,
  Quiz as QuizIcon,
  Poll as SurveyIcon,
} from "@mui/icons-material";

import SearchFoodManager from "./SearchFoodManager";
import AddFoodForm from "./AddFoodForm";
import CategoryManager from "./CategoryManager";
import CategoryDetail from "./CategoryList";
import AddCategoryForm from "./AddCategoryForm";
import Suggestions from "./Suggestions";
import RecipeManager from "./RecipeManager";
import AddRecipeForm from "./AddRecipeForm";
import FoodDetail from "./FoodDetail";
import FoodManager from "./FoodManager";
import HandbookManager from "./HandbookManager";
import SocialTariffManager from "./SocialTariffManager";
import TagManager from "./TagManager";
import AIInsightsManager from "./AIInsightsManager";
import GeneralInstructionsManager from "./GeneralInstructionsManager";
import SloganManager from "./SloganManager";
import CommunityModerationManager from "./CommunityModerationManager";
import GlucoseThresholdManager from "./GlucoseThresholdManager";
import UserManagementManager from "./UserManagementManager";
import QuizManager from "./QuizManager";
import SurveyResponsesManager from "./SurveyResponsesManager";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Create a component that will use Router hooks
function AppContent() {
  const [tabValue, setTabValue] = useState(0);
  const { authenticated, loading, user, logout, login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const location = useLocation();
  const [foodMenuAnchor, setFoodMenuAnchor] = useState(null);
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState(null);
  const [recipeMenuAnchor, setRecipeMenuAnchor] = useState(null);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);

  // Auto-select the correct tab based on path
  useEffect(() => {
    if (location.pathname === "/" || location.pathname.startsWith("/food/") ||
        location.pathname === "/food-manager") {
      setTabValue(0);
    } else if (location.pathname === "/categories" ||
               location.pathname.startsWith("/category/") ||
               location.pathname === "/add-category") {
      setTabValue(1);
    } else if (location.pathname === "/slogans") {
      setTabValue(13);
    } else if (location.pathname === "/recipes" ||
               location.pathname === "/add-recipe") {
      setTabValue(2);
    } else if (location.pathname === "/handbooks") {
      setTabValue(3);
    } else if (location.pathname === "/user-management") {
      setTabValue(4);
    } else if (location.pathname.startsWith("/suggestions") ||
               location.pathname === "/social-tariff" ||
               location.pathname === "/tags" ||
               location.pathname === "/ai-insights" ||
               location.pathname === "/general-instructions" ||
               location.pathname === "/community-moderation" ||
               location.pathname === "/quiz-manager") {
      setTabValue(5);
    }
  }, [location.pathname]);

  // Show loading spinner while Keycloak initializes
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading authentication...</Typography>
      </Box>
    );
  }

  // If not authenticated, show a message instead of redirecting
  if (!authenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <Typography variant="h5" gutterBottom>Authentication Required</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Please log in with proper admin credentials to access the Food Management Portal.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            onClick={() => login ? login() : window.location.reload()} 
            sx={{ mt: 2 }}
          >
            Login
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => window.location.reload()} 
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const toggleDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const drawer = (
    <>
      <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
        <Avatar
          src={"/images/logo.png"}
          alt="Logo"
          sx={{ width: 40, height: 40, mr: 2 }}
        />
        <Typography variant="h6" color="primary">
          Food Management
        </Typography>
      </Box>
      <Divider />
      <List>
        <ListItem
          button
          component={Link}
          to="/"
          selected={tabValue === 0}
          onClick={() => handleTabChange(null, 0)}
        >
          <ListItemIcon>
            <SearchIcon />
          </ListItemIcon>
          <ListItemText primary="Search Food" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/food-manager"
          selected={tabValue === 1}
          onClick={() => handleTabChange(null, 1)}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Food Management" />
        </ListItem>
      </List>
      <Divider />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Category Management
        </Typography>
      </Box>
      <List>
        <ListItem
          button
          component={Link}
          to="/categories"
          selected={tabValue === 2}
          onClick={() => handleTabChange(null, 2)}
        >
          <ListItemIcon>
            <CategoryIcon />
          </ListItemIcon>
          <ListItemText primary="Categories" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/add-category"
          selected={tabValue === 3}
          onClick={() => handleTabChange(null, 3)}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Create Category" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/slogans"
          selected={tabValue === 13}
          onClick={() => handleTabChange(null, 13)}
        >
          <ListItemIcon>
            <SloganIcon />
          </ListItemIcon>
          <ListItemText primary="Slogan Management" />
        </ListItem>
      </List>
      <Divider />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Recipe Management
        </Typography>
      </Box>
      <List>
        <ListItem
          button
          component={Link}
          to="/recipes"
          selected={tabValue === 5}
          onClick={() => handleTabChange(null, 5)}
        >
          <ListItemIcon>
            <RestaurantIcon />
          </ListItemIcon>
          <ListItemText primary="Recipes" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/add-recipe"
          selected={tabValue === 6}
          onClick={() => handleTabChange(null, 6)}
        >
          <ListItemIcon>
            <AddIcon />
          </ListItemIcon>
          <ListItemText primary="Add Recipe" />
        </ListItem>
      </List>
      <Divider />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Handbook Management
        </Typography>
      </Box>
      <List>
        <ListItem
          button
          component={Link}
          to="/handbooks"
          selected={tabValue === 7}
          onClick={() => handleTabChange(null, 7)}
        >
          <ListItemIcon>
            <BookIcon />
          </ListItemIcon>
          <ListItemText primary="Handbooks" />
        </ListItem>
      </List>
      <Divider />
      <List>
        <ListItem
          button
          component={Link}
          to="/user-management"
          selected={tabValue === 4}
          onClick={() => handleTabChange(null, 4)}
        >
          <ListItemIcon>
            <PeopleIcon />
          </ListItemIcon>
          <ListItemText primary="User Management" />
        </ListItem>
      </List>
      <Divider />
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Administration
        </Typography>
      </Box>
      <List>
        <ListItem
          button
          component={Link}
          to="/social-tariff"
          selected={tabValue === 4}
          onClick={() => handleTabChange(null, 4)}
        >
          <ListItemIcon>
            <AssignmentIcon />
          </ListItemIcon>
          <ListItemText primary="Social Tariff" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/tags"
          selected={tabValue === 4}
          onClick={() => handleTabChange(null, 4)}
        >
          <ListItemIcon>
            <TagIcon />
          </ListItemIcon>
          <ListItemText primary="Tag Management" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/ai-insights"
          selected={tabValue === 4}
          onClick={() => handleTabChange(null, 4)}
        >
          <ListItemIcon>
            <AIIcon />
          </ListItemIcon>
          <ListItemText primary="AI Insights" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/general-instructions"
          selected={tabValue === 12}
          onClick={() => handleTabChange(null, 12)}
        >
          <ListItemIcon>
            <BookIcon />
          </ListItemIcon>
          <ListItemText primary="General Instructions" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/community-moderation"
          selected={tabValue === 14}
          onClick={() => handleTabChange(null, 14)}
        >
          <ListItemIcon>
            <AssignmentIcon />
          </ListItemIcon>
          <ListItemText primary="Community Moderation" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/suggestions"
          selected={tabValue === 15}
          onClick={() => handleTabChange(null, 15)}
        >
          <ListItemIcon>
            <EditIcon />
          </ListItemIcon>
          <ListItemText primary="Suggestions" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/quiz-manager"
          selected={location.pathname === "/quiz-manager"}
          onClick={() => { handleTabChange(null, 5); }}
        >
          <ListItemIcon>
            <QuizIcon />
          </ListItemIcon>
          <ListItemText primary="Quiz Manager" />
        </ListItem>
        <ListItem
          button
          component={Link}
          to="/survey-responses"
          selected={location.pathname === "/survey-responses"}
          onClick={() => { handleTabChange(null, 5); }}
        >
          <ListItemIcon>
            <SurveyIcon />
          </ListItemIcon>
          <ListItemText primary="Survey Responses" />
        </ListItem>
      </List>
    </>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <AppBar position="static">
        <Container maxWidth="xl">
          <Toolbar>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={toggleDrawer}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            {/* Added Logo */}
            <Avatar
              src="/images/logo.png"
              alt="Logo"
              sx={{ width: 40, height: 40, mr: 2, display: "flex"}}
            />
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                flexGrow: 1, 
                fontWeight: 'bold', 
                color: 'white' 
              }}
            >
              Food Management App
            </Typography>
            <IconButton
              color="inherit"
              onClick={logout}
              title={`Logout (${user?.preferred_username || user?.email})`}
              sx={{ 
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              <LogoutIcon />
            </IconButton>
          </Toolbar>
          {!isMobile && (
            <Box sx={{ bgcolor: "primary.main", width: "100%" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                textColor="inherit"
                indicatorColor="secondary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": { color: "white" },
                  "& .Mui-selected": { color: "white", fontWeight: "bold" },
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Food Management
                      <ExpandMoreIcon sx={{ ml: 0.5 }} />
                    </Box>
                  }
                  onClick={(e) => setFoodMenuAnchor(e.currentTarget)}
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Categories
                      <ExpandMoreIcon sx={{ ml: 0.5 }} />
                    </Box>
                  }
                  onClick={(e) => setCategoryMenuAnchor(e.currentTarget)}
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Recipes
                      <ExpandMoreIcon sx={{ ml: 0.5 }} />
                    </Box>
                  }
                  onClick={(e) => setRecipeMenuAnchor(e.currentTarget)}
                />
                <Tab label="Handbooks" component={Link} to="/handbooks" />
                <Tab label="User Management" component={Link} to="/user-management" />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Admin
                      <ExpandMoreIcon sx={{ ml: 0.5 }} />
                    </Box>
                  }
                  onClick={(e) => setAdminMenuAnchor(e.currentTarget)}
                />
              </Tabs>
              
              {/* Food Management Dropdown */}
              <Menu
                anchorEl={foodMenuAnchor}
                open={Boolean(foodMenuAnchor)}
                onClose={() => setFoodMenuAnchor(null)}
              >
                <MenuItem component={Link} to="/" onClick={() => setFoodMenuAnchor(null)}>
                  Search Food
                </MenuItem>
                <MenuItem component={Link} to="/food-manager" onClick={() => setFoodMenuAnchor(null)}>
                  Manage Foods
                </MenuItem>
              </Menu>
              
              {/* Categories Dropdown */}
              <Menu
                anchorEl={categoryMenuAnchor}
                open={Boolean(categoryMenuAnchor)}
                onClose={() => setCategoryMenuAnchor(null)}
              >
                <MenuItem component={Link} to="/categories" onClick={() => setCategoryMenuAnchor(null)}>
                  View Categories
                </MenuItem>
                <MenuItem component={Link} to="/add-category" onClick={() => setCategoryMenuAnchor(null)}>
                  Create Category
                </MenuItem>
              </Menu>
              
              {/* Recipes Dropdown */}
              <Menu
                anchorEl={recipeMenuAnchor}
                open={Boolean(recipeMenuAnchor)}
                onClose={() => setRecipeMenuAnchor(null)}
              >
                <MenuItem component={Link} to="/recipes" onClick={() => setRecipeMenuAnchor(null)}>
                  View Recipes
                </MenuItem>
                <MenuItem component={Link} to="/add-recipe" onClick={() => setRecipeMenuAnchor(null)}>
                  Add Recipe
                </MenuItem>
              </Menu>
              
              {/* Admin Dropdown */}
              <Menu
                anchorEl={adminMenuAnchor}
                open={Boolean(adminMenuAnchor)}
                onClose={() => setAdminMenuAnchor(null)}
              >
                <MenuItem component={Link} to="/suggestions" onClick={() => setAdminMenuAnchor(null)}>
                  Suggestions
                </MenuItem>
                <MenuItem component={Link} to="/social-tariff" onClick={() => setAdminMenuAnchor(null)}>
                  Social Tariff
                </MenuItem>
                <MenuItem component={Link} to="/tags" onClick={() => setAdminMenuAnchor(null)}>
                  Tag Management
                </MenuItem>
                <MenuItem component={Link} to="/slogans" onClick={() => setAdminMenuAnchor(null)}>
                  Slogan Management
                </MenuItem>
                <MenuItem component={Link} to="/ai-insights" onClick={() => setAdminMenuAnchor(null)}>
                  AI Insights
                </MenuItem>
                <MenuItem component={Link} to="/general-instructions" onClick={() => setAdminMenuAnchor(null)}>
                  General Instructions
                </MenuItem>
                <MenuItem component={Link} to="/community-moderation" onClick={() => setAdminMenuAnchor(null)}>
                  Community Moderation
                </MenuItem>
                <MenuItem component={Link} to="/glucose-thresholds" onClick={() => setAdminMenuAnchor(null)}>
                  Glucose Thresholds
                </MenuItem>
                <MenuItem component={Link} to="/quiz-manager" onClick={() => setAdminMenuAnchor(null)}>
                  Quiz Manager
                </MenuItem>
                <MenuItem component={Link} to="/survey-responses" onClick={() => setAdminMenuAnchor(null)}>
                  Survey Responses
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Container>
      </AppBar>

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={toggleDrawer}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            "& .MuiDrawer-paper": { width: 280 },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Container component="main" maxWidth="xl" sx={{ flexGrow: 1, py: 3 }}>
        <Routes>
          <Route path="/" element={<SearchFoodManager />} />
          <Route path="/food-detail/:id" element={<FoodDetail />} />
          <Route path="/food-manager" element={<FoodManager />} />
          <Route path="/add-food" element={<AddFoodForm />} />
          <Route path="/categories" element={<CategoryManager />} />
          <Route path="/category/:id" element={<CategoryDetail />} />
          <Route path="/add-category" element={<AddCategoryForm />} />
          <Route path="/suggestions/*" element={<Suggestions />} />
          <Route path="/recipes" element={<RecipeManager />} />
          <Route path="/add-recipe" element={<AddRecipeForm />} />
          <Route path="/handbooks" element={<HandbookManager />} />
          <Route path="/social-tariff" element={<SocialTariffManager />} />
          <Route path="/tags" element={<TagManager />} />
          <Route path="/ai-insights" element={<AIInsightsManager />} />
          <Route path="/general-instructions" element={<GeneralInstructionsManager />} />
          <Route path="/slogans" element={<SloganManager />} />
          <Route path="/community-moderation" element={<CommunityModerationManager />} />
          <Route path="/glucose-thresholds" element={<GlucoseThresholdManager />} />
          <Route path="/user-management" element={<UserManagementManager />} />
          <Route path="/quiz-manager" element={<QuizManager />} />
          <Route path="/survey-responses" element={<SurveyResponsesManager />} />

          <Route
            path="/category-edit"
            element={<CategoryManager createMode={true} />}
          />
          <Route path="/category-edit/:id" element={<CategoryManager />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Container>
    </Box>
  );
}

// Main App component that wraps everything in Router and AuthProvider
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
